import { faBirthdayCake, faPenToSquare } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Typography, IconButton, useTheme, Grid, Tooltip } from "@mui/material";
import { GridColDef } from "@mui/x-data-grid";
import React, { useCallback, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import DialogActions from "src/containers/DialogActions/DialogActions";
import LoadingOverlay from "src/containers/LoadingOverlay/LoadingOverlay";
import {
  AnomalyTypesQuickfilter,
  useAnomalyTypesQuickFilter,
} from "src/containers/Quickfilters/AnomalyTypesQuickfilter";
import { useEmployeeGroupQuickFilter } from "src/containers/Quickfilters/EmployeeGroupQuickfilter";
import EmployeeGroupQuickfilter from "src/containers/Quickfilters/EmployeeGroupQuickfilter/EmployeeGroupQuickfilter";
import { EmployeesQuickfilter } from "src/containers/Quickfilters/EmployeesQuickfilter/EmployeesQuickfilter";
import { useEmployeesQuickFilter } from "src/containers/Quickfilters/EmployeesQuickfilter/useEmployeesQuickFilter";
import { LegalEntityQuickfilter, useLegalEntityQuickFilter } from "src/containers/Quickfilters/LegalEntityQuickfilter";
import {
  NotificationStatusQuickfilter,
  useNotificationStatusQuickFilter,
} from "src/containers/Quickfilters/NotificationStatusQuickfilter";
import QuickfiltersContainer from "src/containers/Quickfilters/QuickfiltersContainer/QuickfiltersContainer";
import useAuthorization from "src/custom-hooks/useAuthorization/useAuthorization";
import { RefModel } from "src/models/base/ref-model";
import { CustomDate } from "src/models/custom-date/custom-date";
import { EmployeeRef } from "src/models/employees";
import { AnomalyTrackingFilter } from "src/models/tna/tna-anomalies/anomaly-tracking-filter";
import { AnomalyTrackingNotificationMetaDataDTO } from "src/models/tna/tna-anomalies/anomaly-tracking-notification-meta-data-dto";
import { AnomalyTrackingNotificationMetaDataSTO } from "src/models/tna/tna-anomalies/anomaly-tracking-notification-meta-data-sto";
import { AnomalyTypeRef } from "src/models/tna/tna-anomalies/anomaly-type/anomaly-type-ref";
import ConcatenatedCell from "src/reusable-components/ConcatenatedCell/ConcatenatedCell";
import { ColumnsHeaderTitle } from "src/reusable-components/Table/styles";
import Table from "src/reusable-components/Table/Table";
import { AnomalyTrackingNotificationMetaData } from "src/services/tna-anomalies/anomalies-tracking-notification-meta-data-api.service";
import { AUTHORIZATIONS } from "src/utils/constants/authorization-constants";
import { FT_TNALT } from "src/utils/constants/filter-tables-constants";
import { intlFormatDateShort } from "src/utils/time-utils";
import { toastifyResponse } from "src/utils/toastify-response/toastify-response";
import { v4 as uuidv4 } from "uuid";
import AddEditAppendedComment from "./AddEditAppendedComment/AddEditAppendedComment";
import AddEditIncludedAnomalyTypes from "./AddEditIncludedAnomalyTypes/AddEditIncludedAnomalyTypes";
import AnomaliesPopover from "./AnomaliesPopover/AnomaliesPopover";
import { EmployeeNameContainer } from "../MyTeamBreaks/styles";
interface Props {
  handleNotifyParent: () => void;
  tableHeight: number;
  handleClose?: () => void;
  employee?: EmployeeRef | null;
}

export interface AnomaliesTypesToInclude {
  employeeId: string;
  anomalyTypes: AnomalyTypeRef[];
}

export interface State {
  loading: boolean;
  error: string;
  localItems: AnomalyTrackingNotificationMetaDataDTO[];
  anomaliesTypesToInclude: AnomaliesTypesToInclude[];
  lastAnomalyNotificationDate: Date | null;
  lastSignoffDate: Date | null;
  employee: EmployeeRef | null;
  allEmployees: boolean;
  employeeQfId: string;
}

const MultipleEmployeesMassAnomaliesListTable = ({ handleNotifyParent, tableHeight, handleClose, employee }: Props) => {
  const { t } = useTranslation();
  const tRP = "components.my_team_breaks.anomalies_list_table"; // tRP - translation root path
  const tRPCommon = "pages.company_account_profile.tables.employees.fields";
  const theme = useTheme();

  // #region quickfilters
  const {
    legalEntitiesQuickFilter,
    setLegalEntitiesQuickFilter,
    selectedLegalEntity,
    legalEntityQFIsLoading,
    allLegalEntities,
  } = useLegalEntityQuickFilter(true);

  const { notificationStatusQuickFilter, setNotificationStatusQuickFilter } = useNotificationStatusQuickFilter();

  const { employeeGroupQuickFilter, setEmployeeGroupQuickFilter } = useEmployeeGroupQuickFilter();

  // const { daysQuickFilter, setDaysQuickFilter, selectedDays } = useChangeDays();

  const { employeesQuickFilter, setEmployeesQuickFilter, allEmployees, employeeQFIsLoading } = useEmployeesQuickFilter(
    true,
    employeeGroupQuickFilter.employeeGroups?.employeeGroupIds ?? [],
    legalEntitiesQuickFilter.legalEntities?.legalEntityIds ?? [],
    !legalEntityQFIsLoading
  );

  const { anomalyTypesQuickFilter, setAnomalyTypesQuickFilter, allAnomalyTypes, anomalyTypeQFIsLoading } =
    useAnomalyTypesQuickFilter(true);

  const hasAccess = useAuthorization([
    AUTHORIZATIONS.EmployeesAccess.CreateEmployees,
    AUTHORIZATIONS.EmployeesAccess.ConfigureEmployeeGroupParameters,
    AUTHORIZATIONS.LegalEntityAccess.ProcessPayroll,
  ]);
  // #endregion

  const [state, setState] = useState<State>({
    loading: false,
    error: "",
    localItems: [],
    anomaliesTypesToInclude: [],
    lastAnomalyNotificationDate: null,
    lastSignoffDate: null,
    employee: null,
    allEmployees: true,
    employeeQfId: uuidv4(),
  });

  const allowedToViewOnly = useAuthorization([AUTHORIZATIONS.EmployeesAccess.ViewNonSalaryAndCompensationData]);

  const selectedRowsDataRef = useRef<AnomalyTrackingNotificationMetaDataDTO[]>([]);

  const handleRowSelection = (selectedItems: AnomalyTrackingNotificationMetaDataDTO[]) => {
    selectedRowsDataRef.current = selectedItems;
  };

  const handleTableDataLoad = useCallback(
    (data: RefModel[]) => {
      if (employee && data.length > 0) {
        const employeeRow = (data as AnomalyTrackingNotificationMetaDataDTO[]).find(
          (row) => row.employee?.id === employee.id
        );
        if (employeeRow) {
          handleRowSelection([employeeRow]);
        }
      }
    },
    [employee]
  );

  // #region handle comment update
  const handleLocalCommentUpdate = useCallback(
    (updatedItem: AnomalyTrackingNotificationMetaDataDTO) => {
      if (state.localItems.find((item) => item.employee?.id === updatedItem.employee?.id)) {
        setState((prev) => ({
          ...prev,
          localItems: [
            ...prev.localItems.filter((item) => item.employee?.id !== updatedItem.employee?.id),
            updatedItem,
          ],
        }));
      } else {
        setState((prev) => ({
          ...prev,
          localItems: [...prev.localItems, updatedItem],
        }));
      }
    },
    [state.localItems]
  );
  // #endregion

  // #region anomaly tye ids update
  const handleLocalAnomalyTypeIdsUpdate = useCallback(
    (updatedItem: AnomalyTrackingNotificationMetaDataDTO, anomalyTypes: AnomalyTypeRef[]) => {
      const employeeId = updatedItem.employee?.id;
      if (!employeeId) return;

      setState((prev) => {
        const existingIndex = prev.anomaliesTypesToInclude.findIndex((item) => item.employeeId === employeeId);
        const updatedAnomaliesTypesToInclude = [...prev.anomaliesTypesToInclude];

        if (existingIndex >= 0) {
          updatedAnomaliesTypesToInclude[existingIndex] = { employeeId, anomalyTypes };
        } else {
          updatedAnomaliesTypesToInclude.push({ employeeId, anomalyTypes });
        }

        return {
          ...prev,
          anomaliesTypesToInclude: updatedAnomaliesTypesToInclude,
        };
      });
    },
    []
  );
  // #endregion

  // #region comment cell renderer
  const CommentCellRenderer = ({ row }: { row: AnomalyTrackingNotificationMetaDataDTO }) => {
    const localItem = state.localItems.find((item) => item.employee?.id === row.employee?.id) || row;

    return (
      <Grid
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          width: "100%",
          height: "100%",
        }}
      >
        {localItem?.lastComment && (
          <Grid
            style={{
              display: "flex",
              alignItems: "center",
              height: "100%",
              width: "calc(100% - 40px)",
            }}
          >
            <Typography
              variant="body1"
              style={{ flex: 1 }}
            >
              {localItem?.lastComment}
            </Typography>
          </Grid>
        )}
        <AddEditAppendedComment
          item={localItem}
          effect="edit"
          onLocalSave={handleLocalCommentUpdate}
          disabled={!hasAccess && allowedToViewOnly}
          viewMode={!hasAccess && allowedToViewOnly}
        >
          <IconButton
            size="small"
            title="Edit comment"
          >
            <FontAwesomeIcon
              size="lg"
              icon={faPenToSquare}
              color={theme.palette.primary.main}
            />
          </IconButton>
        </AddEditAppendedComment>
      </Grid>
    );
  };
  // #endregion

  const CommentCellMemoizedRenderer = React.memo(CommentCellRenderer, (prevProps, nextProps) => {
    const prevLocalItem = state.localItems.find((item) => item.id === prevProps.row.id) || prevProps.row;
    const nextLocalItem = state.localItems.find((item) => item.id === nextProps.row.id) || nextProps.row;
    return prevLocalItem?.lastComment === nextLocalItem?.lastComment;
  });

  // #region anomalies to include
  const AnomaliesToIncludeCellRenderer = ({ row }: { row: AnomalyTrackingNotificationMetaDataDTO }) => {
    const selectedTypes = React.useMemo(() => {
      if (!row.employee?.id) return [];
      return state.anomaliesTypesToInclude.find((item) => item.employeeId === row.employee?.id)?.anomalyTypes || [];
    }, [row.employee?.id]);

    return (
      <Grid
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          width: "100%",
          height: "100%",
          gap: "5px",
        }}
      >
        {/* <Grid
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: 20,
            height: "100%",
            width: "100%",
          }}
        > */}
        {selectedTypes.length === 0 ? (
          <Typography variant="body1">{t("common_words.all")}</Typography>
        ) : (
          <Grid
            container
            direction={"column"}
            style={{ width: "100%" }}
          >
            {selectedTypes.map((anomalyType) => (
              <Typography
                key={anomalyType.id}
                style={{
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  maxWidth: "100%",
                  fontSize: "11px",
                  textWrap: "wrap",
                }}
              >
                &#8226; {anomalyType.name}
              </Typography>
            ))}
          </Grid>
        )}
        <AddEditIncludedAnomalyTypes
          item={row}
          effect="edit"
          onLocalSave={(item, types) => handleLocalAnomalyTypeIdsUpdate(item, types)}
          disabled={!hasAccess && allowedToViewOnly}
          viewMode={!hasAccess && allowedToViewOnly}
          selectedAnomalyTypes={selectedTypes}
        >
          <IconButton
            size="small"
            title="Edit included anomaly types"
          >
            <FontAwesomeIcon
              size="lg"
              icon={faPenToSquare}
              color={theme.palette.primary.main}
            />
          </IconButton>
        </AddEditIncludedAnomalyTypes>
      </Grid>
      // </Grid>
    );
  };
  // #endregion

  const AnomaliesToIncludeMemoizedRenderer = React.memo(AnomaliesToIncludeCellRenderer, (prevProps, nextProps) => {
    const prevEmployeeId = prevProps.row.employee?.id;
    const nextEmployeeId = nextProps.row.employee?.id;

    if (prevEmployeeId !== nextEmployeeId) return false;
    if (!prevEmployeeId) return true;

    const prevFound = state.anomaliesTypesToInclude.find((item) => item.employeeId === prevEmployeeId);
    const nextFound = state.anomaliesTypesToInclude.find((item) => item.employeeId === nextEmployeeId);

    const prevSelected = (prevFound?.anomalyTypes ?? []).map((type) => type.id);
    const nextSelected = (nextFound?.anomalyTypes ?? []).map((type) => type.id);

    return prevSelected.length === nextSelected.length && prevSelected.every((id) => nextSelected.includes(id));
  });

  // #region save
  const handleSave = useCallback(async () => {
    if (selectedRowsDataRef.current.length === 0) {
      const data = {
        id: "",
        correlationId: "",
        invokerId: "",
        isSucceeded: false,
        isCanceled: false,
        error: t("components.toast.no_employee_anomalies_selected"),
      };
      toastifyResponse(data, "failed");
      return;
    }

    setState((prev) => ({ ...prev, loading: true }));

    try {
      const apiService = new AnomalyTrackingNotificationMetaData();

      const selectedNotificationRowsData = selectedRowsDataRef.current.map((selectedItem) => {
        const latestItem =
          state.localItems.find((item) => item.employee?.id === selectedItem.employee?.id) || selectedItem;
        const employeeId = latestItem.employee?.id;
        const anomalyTypes = employeeId
          ? state.anomaliesTypesToInclude.find((item) => item.employeeId === employeeId)?.anomalyTypes || []
          : [];

        return {
          id: uuidv4() || latestItem.id,
          //   companyAccount: latestItem.companyAccount,
          //   legalEntity: latestItem.legalEntity,
          lastNotifiedOn: latestItem.lastNotifiedOn,
          employee: latestItem.employee,
          lastComment: latestItem.lastComment,
          anomalyTypes,
        } as AnomalyTrackingNotificationMetaDataSTO;
      });

      const response = await apiService.saveItems(selectedNotificationRowsData);

      if (response.isSucceeded) {
        selectedRowsDataRef.current = [];
        setState((prev) => ({
          ...prev,
          loading: false,
          error: "",
        }));
        toastifyResponse(response, "all", "employees_notified_successfully");
        handleNotifyParent?.();
      }
    } catch (error) {
      selectedRowsDataRef.current = [];
      setState((prev) => ({
        ...prev,
        error: error as string,
        loading: false,
      }));
      console.error(error);
    } finally {
      setState((prev) => ({ ...prev, loading: false }));
    }
  }, [handleNotifyParent, state.localItems, state.anomaliesTypesToInclude]);

  // #region columns
  const columns: GridColDef<AnomalyTrackingNotificationMetaDataDTO>[] = [
    // {
    //   field: "employee",
    //   headerName: `${t("common_words.employee")} ${t("common_words.legal_entity")}`,
    //   headerAlign: "center",
    //   flex: 1,
    //   align: "left",
    //   // valueGetter: ({ row }) => row?.employee?.name || "",
    //   renderCell: ({ row }) => (
    //     <Grid sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
    //       <Typography>{row?.employee?.name}</Typography>
    //       <Typography>{row?.legalEntity?.name}</Typography>
    //     </Grid>
    //   ),
    // },
    {
      field: "employeeCode",
      renderHeader: () => (
        <Typography>{`${t(`${tRPCommon}.employee_code`)} | ${t(`${tRPCommon}.internal_id`)}`}</Typography>
      ),
      headerAlign: "center",
      flex: 0.5,
      minWidth: 80,
      renderCell: ({ row }) => (
        <ConcatenatedCell
          aligned={"center"}
          FirstElement={
            <Tooltip title={`${t(`${tRPCommon}.employee_code`)}`}>
              <Typography>{row.employee?.employeeCode ?? ""}</Typography>
            </Tooltip>
          }
          SecondElement={
            <Tooltip title={`${t(`${tRPCommon}.internal_id`)}`}>
              <Typography
                style={{ color: theme.palette.text.disabled }}
                variant={"body2"}
              >
                {row.employee?.internalId ?? ""}
              </Typography>
            </Tooltip>
          }
        />
      ),
    },
    {
      field: "name",
      renderCell: ({ row }) => (
        <ConcatenatedCell
          aligned={"left"}
          FirstElement={
            <EmployeeNameContainer>
              <Grid sx={{ display: "flex", flexDirection: "column" }}>
                <span title={row?.employee?.name}>{row?.employee?.name}</span>
                {row?.employee?.birthDate && (
                  <Typography variant={"body2"}>
                    <FontAwesomeIcon
                      icon={faBirthdayCake}
                      size="xs"
                    />{" "}
                    &nbsp;
                    {intlFormatDateShort(row.employee.birthDate)}
                  </Typography>
                )}
              </Grid>
            </EmployeeNameContainer>
          }
          SecondElement={
            <Grid>
              <Typography
                style={{ color: theme.palette.text.disabled }}
                variant={"body2"}
                title={row?.employee?.legalEntity?.name}
              >
                {row?.employee?.legalEntity?.code ?? row?.employee?.legalEntity?.name}
              </Typography>
            </Grid>
          }
        />
      ),
      renderHeader: () => (
        <Typography>{`${t("common_words.employee")} | ${t("common_words.legal_entity")}`}</Typography>
      ),
      headerAlign: "center",
      flex: 1,
      minWidth: 150,
    },
    {
      field: "tnAGroup",
      renderHeader: () => (
        <Tooltip title={`${t("common_words.tna_employee_group")}`}>
          <ColumnsHeaderTitle>{`${t("common_words.tna_employee_group")}`}</ColumnsHeaderTitle>
        </Tooltip>
      ),
      headerAlign: "center",
      flex: 0.5,
      minWidth: 140,
      sortable: false,
      disableColumnMenu: true,
      renderCell: ({ row }) => (
        <Tooltip title={row.employee?.tnAGroup?.name}>
          <Typography
            key={row.employee?.tnAGroup?.id}
            sx={{ margin: "0 5px" }}
          >
            {row.employee?.tnAGroup?.code}
          </Typography>
        </Tooltip>
      ),
    },
    {
      field: "lastNotifiedOn",
      headerName: t("common_words.last_notified_on"),
      headerAlign: "center",
      sortable: false,
      disableColumnMenu: true,
      valueGetter: ({ row }) =>
        row.lastNotifiedOn ? intlFormatDateShort(CustomDate.ToDate(row.lastNotifiedOn) as Date) : "",
      flex: 1.5,
      align: "center",
    },
    {
      field: "daysWithAnomalies",
      headerName: t(`${tRP}.days_with_anomalies`),
      headerAlign: "center",
      sortable: false,
      disableColumnMenu: true,
      flex: 3.2,
      align: "center",
      renderCell: ({ row }) => <AnomaliesPopover anomalies={row.anomalies ?? []} />,
    },
    // {
    //   field: "anomalyDate",
    //   headerName: t("common_words.date"),
    //   headerAlign: "center",
    //   sortable: true,
    //   disableColumnMenu: true,
    //   valueGetter: ({ row }) => intlFormatDateShort(new Date(row?.year, row?.month - 1, row?.day)),
    //   flex: 1,
    //   align: "center",
    // },
    // {
    //   field: "anomalyDescription",
    //   headerName: t("common_words.anomaly"),
    //   headerAlign: "center",
    //   sortable: false,
    //   disableColumnMenu: true,
    //   valueGetter: ({ row }) => row?.anomalyType?.description ?? "",
    //   flex: 2,
    //   align: "left",
    // },
    {
      field: "anomaliesToInclude",
      headerName: t(`${tRP}.anomalies_to_include`),
      headerAlign: "center",
      sortable: false,
      disableColumnMenu: true,
      renderCell: ({ row }) => <AnomaliesToIncludeMemoizedRenderer row={row} />,
      flex: 3.2,
      align: "center",
    },
    {
      field: "comment",
      // headerName: t("common_words.comment"),
      renderHeader: () => (
        <div style={{ display: "flex", flexDirection: "column" }}>
          <Typography>{t("common_words.comment")}</Typography>
          <Typography
            variant={"subtitle1"}
            sx={{ fontSize: "11px !important", lineHeight: "1.2 !important" }}
          >
            ({t(`${tRP}.comment_notification`)})
          </Typography>
        </div>
      ),
      headerAlign: "center",
      sortable: false,
      disableColumnMenu: true,
      renderCell: ({ row }) => <CommentCellMemoizedRenderer row={row} />,
      flex: 1.8,
      align: "center",
    },
  ];

  // #region quickfilters components
  const quickFilters = React.useMemo(
    () =>
      !employee && (
        <QuickfiltersContainer>
          <LegalEntityQuickfilter
            quickFilters={legalEntitiesQuickFilter}
            handleChangeQuickFilters={setLegalEntitiesQuickFilter}
            allLegalEntitiesOption
            allLegalEntities={allLegalEntities}
          />
          <EmployeeGroupQuickfilter
            quickFilters={employeeGroupQuickFilter}
            handleChangeQuickFilters={setEmployeeGroupQuickFilter}
            allEmployeeGroupsOption
          />
          <EmployeesQuickfilter
            quickFilters={employeesQuickFilter}
            handleChangeQuickFilters={setEmployeesQuickFilter}
            allEmployeesOption
            allEmployees={allEmployees}
            isMultiSelect
          />
          <AnomalyTypesQuickfilter
            quickFilters={anomalyTypesQuickFilter}
            handleChangeQuickFilters={setAnomalyTypesQuickFilter}
            allAnomalyTypesOption
            allAnomalyTypes={allAnomalyTypes}
            isMultiSelect
          />
          <NotificationStatusQuickfilter
            quickFilters={notificationStatusQuickFilter}
            handleChangeQuickFilters={setNotificationStatusQuickFilter}
          />
          {/* <FilterByDays
          quickFilters={daysQuickFilter}
          handleChangeQuickFilters={setDaysQuickFilter}
          filterColor="filter"
          withMaxDate={false}
          maxSelectableDays={31}
        /> */}
        </QuickfiltersContainer>
      ),
    [
      employee,
      legalEntitiesQuickFilter,
      setLegalEntitiesQuickFilter,
      allLegalEntities,
      employeeGroupQuickFilter,
      setEmployeeGroupQuickFilter,
      employeesQuickFilter,
      setEmployeesQuickFilter,
      allEmployees,
      anomalyTypesQuickFilter,
      setAnomalyTypesQuickFilter,
      allAnomalyTypes,
      notificationStatusQuickFilter,
      setNotificationStatusQuickFilter,
    ]
  );
  // #endregion

  // #region propsflter
  const propsFilter = React.useMemo(
    () =>
      ({
        employeesIds: employee?.id ? [employee.id] : employeesQuickFilter.employees?.employeeIds || [],
        legalEntityIds: selectedLegalEntity?.id ? [selectedLegalEntity.id] : [],
        employeeGroupIds: employeeGroupQuickFilter.employeeGroups?.employeeGroupIds || [],
        typeIds: anomalyTypesQuickFilter.anomalyTypes?.anomalyTypeIds || [],
        isNotified: notificationStatusQuickFilter.notificationStatus?.isNotified,
        isResolved: false,
      } as AnomalyTrackingFilter),
    [
      employee?.id,
      employeesQuickFilter.employees?.employeeIds,
      selectedLegalEntity?.id,
      employeeGroupQuickFilter.employeeGroups?.employeeGroupIds,
      anomalyTypesQuickFilter.anomalyTypes?.anomalyTypeIds,
      notificationStatusQuickFilter.notificationStatus?.isNotified,
    ]
  );

  return (
    <LoadingOverlay loading={state.loading || legalEntityQFIsLoading || employeeQFIsLoading || anomalyTypeQFIsLoading}>
      <Table
        hideRemoveIcon
        translationRootPath={tRP}
        displayTitle={false}
        columns={columns}
        allowEdit={false}
        readOnly
        defaultPageSize={10}
        tableHeight={tableHeight}
        serviceApi={new AnomalyTrackingNotificationMetaData()}
        enableSelection={!employee}
        handleCheckedRows={handleRowSelection}
        propsFilter={propsFilter}
        {...(quickFilters && { quickFilters })}
        filter={FT_TNALT}
        depthSelection="employee"
        hideSearch={!!employee}
        getAllItems={handleTableDataLoad}
      />
      <DialogActions
        handleCancel={() => {
          selectedRowsDataRef.current = [];
          setState((prev) => ({ ...prev, error: "" }));
          handleClose?.();
        }}
        handleSave={handleSave}
        disabledSaveButton={state.loading}
        disabledCancelButton={state.loading}
        customSaveName={t(`${tRP}.send_notifications`)}
      />
    </LoadingOverlay>
  );
};

export default React.memo(MultipleEmployeesMassAnomaliesListTable);
